import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2, Send, Pause, Play, X as XIcon, Eye, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Switch } from "@/components/ui/switch";
import {
  getUpgradeSequence,
  updateSequence,
  updateStep,
  previewStep,
  sendTestEmail,
  listEnrollments,
  setEnrollmentStatus,
  getEnrollmentHistory,
  getSequenceAnalytics,
} from "@/server/email-sequences-admin.functions";

export const Route = createFileRoute("/dashboard/admin/comms/email-automations/upgrade")({
  head: () => ({
    meta: [
      { title: "Provider Upgrade Sequence · Admin" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: Page,
});

const DAY_OFFSETS = [0, 3, 10, 13, 20, 23, 30];

function Stat({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <div className="card-surface-flat rounded-lg p-4">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="text-[22px] font-semibold tabular-nums mt-1">{value}</div>
      {hint && <div className="text-[11px] text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

function Page() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<"overview" | "steps" | "enrollments">("overview");

  const getSeq = useServerFn(getUpgradeSequence);
  const getStats = useServerFn(getSequenceAnalytics);

  const { data: seqData, isLoading } = useQuery({
    queryKey: ["upgrade-sequence"],
    queryFn: () => getSeq(),
  });
  const { data: stats } = useQuery({
    queryKey: ["upgrade-sequence-stats"],
    queryFn: () => getStats(),
    refetchInterval: 30_000,
  });

  const updSeq = useServerFn(updateSequence);
  const toggleMut = useMutation({
    mutationFn: (patch: any) => updSeq({ data: { id: seqData!.sequence.id, ...patch } }),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["upgrade-sequence"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Update failed"),
  });

  if (isLoading || !seqData) {
    return (
      <div className="flex items-center justify-center py-10">
        <Loader2 className="h-4 w-4 animate-spin" />
      </div>
    );
  }

  const seq = seqData.sequence;
  const steps = seqData.steps;

  return (
    <div className="space-y-4">
      {/* Status banner */}
      <div className="card-surface p-4 flex items-center justify-between gap-4">
        <div>
          <div className="text-[14px] font-semibold font-display">{seq.name}</div>
          <div className="text-[12px] text-muted-foreground mt-0.5">
            Day 0 · Day 3 · Day 10 · Day 13 · Day 20 · Day 23 · Day 30 — 7 emails total
          </div>
        </div>
        <div className="flex items-center gap-5">
          <label className="flex items-center gap-2 text-[12.5px]">
            <Switch
              checked={seq.enabled}
              onCheckedChange={(v) => toggleMut.mutate({ enabled: v })}
            />
            <span>{seq.enabled ? "Sequence enabled" : "Sequence disabled"}</span>
          </label>
          <label className="flex items-center gap-2 text-[12.5px]">
            <Switch
              checked={seq.test_mode}
              onCheckedChange={(v) => toggleMut.mutate({ test_mode: v })}
            />
            <span>{seq.test_mode ? "Test mode (no real emails)" : "Live sending"}</span>
          </label>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-1 border-b border-border">
        {(["overview", "steps", "enrollments"] as const).map((t) => (
          <button
            key={t}
            onClick={() => setTab(t)}
            className={
              "relative px-3 py-2 text-[12.5px] capitalize -mb-px " +
              (tab === t ? "text-primary font-semibold" : "text-muted-foreground hover:text-foreground font-medium")
            }
          >
            {t}
            {tab === t && <span className="absolute left-2 right-2 -bottom-px h-[2px] bg-primary rounded-full" />}
          </button>
        ))}
      </div>

      {tab === "overview" && <OverviewTab stats={stats} />}
      {tab === "steps" && <StepsTab steps={steps} />}
      {tab === "enrollments" && <EnrollmentsTab />}
    </div>
  );
}

function OverviewTab({ stats }: { stats: any }) {
  if (!stats) return <p className="text-[13px] text-muted-foreground">No stats yet.</p>;
  const sent = stats.sent || 0;
  const clicks = stats.clicked || 0;
  const ctr = sent > 0 ? `${((clicks / sent) * 100).toFixed(1)}%` : "—";
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <Stat label="Enrolled" value={stats.enrolled} />
      <Stat label="Active in sequence" value={stats.active} />
      <Stat label="Emails sent" value={sent} />
      <Stat label="Click rate" value={ctr} hint={`${clicks} clicks`} />
      <Stat label="Upgrade started" value={stats.upgrade_started} />
      <Stat label="Upgrade completed" value={stats.upgrade_completed} />
      <Stat label="Stopped — upgraded" value={stats.stopped_upgraded} />
      <Stat label="Stopped — completed flow" value={stats.completed} />
      <Stat label="Unsubscribes" value={stats.unsubscribes} />
      <Stat label="Bounces" value={stats.bounces} />
      <Stat label="Paused" value={stats.paused} />
      <Stat label="Removed by admin" value={stats.stopped_admin} />
    </div>
  );
}

function StepsTab({ steps }: { steps: any[] }) {
  return (
    <div className="space-y-3">
      {steps.map((s, i) => (
        <StepCard key={s.id} step={s} dayOffset={DAY_OFFSETS[i] ?? null} />
      ))}
    </div>
  );
}

function StepCard({ step, dayOffset }: { step: any; dayOffset: number | null }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [subject, setSubject] = useState(step.subject);
  const [body, setBody] = useState(step.body_markdown);
  const [cta, setCta] = useState(step.cta_text);
  const [testEmail, setTestEmail] = useState("");
  const [previewHtml, setPreviewHtml] = useState<string | null>(null);

  const upd = useServerFn(updateStep);
  const prev = useServerFn(previewStep);
  const test = useServerFn(sendTestEmail);

  const saveMut = useMutation({
    mutationFn: () => upd({ data: { id: step.id, subject, body_markdown: body, cta_text: cta } }),
    onSuccess: () => {
      toast.success("Saved");
      qc.invalidateQueries({ queryKey: ["upgrade-sequence"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Save failed"),
  });

  const previewMut = useMutation({
    mutationFn: () => prev({ data: { stepId: step.id } }),
    onSuccess: (r: any) => setPreviewHtml(r.html),
  });

  const testMut = useMutation({
    mutationFn: () => test({ data: { stepId: step.id, toEmail: testEmail } }),
    onSuccess: () => toast.success(`Test email sent to ${testEmail}`),
    onError: (e: any) => toast.error(e.message ?? "Test failed"),
  });

  return (
    <div className="card-surface-flat">
      <button
        onClick={() => setOpen(!open)}
        className="w-full px-4 py-3 flex items-center justify-between text-left"
      >
        <div>
          <div className="text-[13px] font-semibold">
            Step {step.step_number}
            {dayOffset !== null && (
              <span className="ml-2 text-[11px] font-normal text-muted-foreground">Day {dayOffset}</span>
            )}
          </div>
          <div className="text-[12.5px] text-muted-foreground mt-0.5">{step.subject}</div>
        </div>
        <span className="text-[11px] text-muted-foreground">{open ? "Hide" : "Edit"}</span>
      </button>

      {open && (
        <div className="p-4 border-t border-border space-y-3">
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1" />
          </div>
          <div>
            <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Body</label>
            <Textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              rows={14}
              className="mt-1 font-mono text-[12.5px]"
            />
            <p className="text-[11px] text-muted-foreground mt-1">
              Variables: {"{{provider_first_name}}"}, {"{{practice_name}}"}, {"{{upgrade_link}}"}, {"{{city}}"}, {"{{specialty}}"}, {"{{profile_link}}"}, {"{{claim_date}}"}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">CTA button text</label>
              <Input value={cta} onChange={(e) => setCta(e.target.value)} className="mt-1" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wide text-muted-foreground">Send a test to</label>
              <div className="flex gap-2 mt-1">
                <Input
                  type="email"
                  placeholder="you@example.com"
                  value={testEmail}
                  onChange={(e) => setTestEmail(e.target.value)}
                />
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!testEmail || testMut.isPending}
                  onClick={() => testMut.mutate()}
                >
                  {testMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                </Button>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button size="sm" onClick={() => saveMut.mutate()} disabled={saveMut.isPending}>
              {saveMut.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
              Save changes
            </Button>
            <Button size="sm" variant="outline" onClick={() => previewMut.mutate()} disabled={previewMut.isPending}>
              <Eye className="h-3.5 w-3.5 mr-1" />
              Preview
            </Button>
          </div>
          {previewHtml && (
            <div className="rounded-md border border-border overflow-hidden mt-2">
              <div className="px-3 py-1.5 text-[11px] uppercase tracking-wide bg-muted/40 text-muted-foreground">Preview</div>
              <iframe srcDoc={previewHtml} className="w-full h-[420px] bg-white" title="preview" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function EnrollmentsTab() {
  const qc = useQueryClient();
  const [status, setStatus] = useState<string>("");
  const [q, setQ] = useState("");
  const [openHistory, setOpenHistory] = useState<string | null>(null);

  const listFn = useServerFn(listEnrollments);
  const setStatusFn = useServerFn(setEnrollmentStatus);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["upgrade-enrollments", status, q],
    queryFn: () => listFn({ data: { status: status || undefined, q: q || undefined, pageSize: 50 } }),
  });

  const actMut = useMutation({
    mutationFn: (vars: { id: string; action: "pause" | "resume" | "remove" }) =>
      setStatusFn({ data: vars }),
    onSuccess: () => {
      toast.success("Updated");
      qc.invalidateQueries({ queryKey: ["upgrade-enrollments"] });
      qc.invalidateQueries({ queryKey: ["upgrade-sequence-stats"] });
    },
    onError: (e: any) => toast.error(e.message ?? "Action failed"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className="h-9 border border-border rounded-md px-2 text-[13px] bg-background"
        >
          <option value="">All statuses</option>
          {["active", "paused", "completed", "stopped_upgraded", "stopped_unsub", "stopped_bounced", "stopped_admin"].map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
        </select>
        <Input
          placeholder="Search by email"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="max-w-[280px]"
        />
        <Button size="sm" variant="ghost" onClick={() => refetch()}>
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      <div className="border border-border rounded-md">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Email</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Current step</TableHead>
              <TableHead>Next send</TableHead>
              <TableHead>Started</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6">
                  <Loader2 className="h-4 w-4 animate-spin inline" />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && (data?.items ?? []).length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center py-6 text-muted-foreground">
                  No enrollments yet.
                </TableCell>
              </TableRow>
            )}
            {(data?.items ?? []).map((e: any) => (
              <TableRow key={e.id}>
                <TableCell className="font-mono text-[12px]">{e.email}</TableCell>
                <TableCell>
                  <span className="text-[10.5px] px-2 py-0.5 rounded-md bg-muted">{e.status}</span>
                </TableCell>
                <TableCell className="tabular-nums">{e.current_step} / 7</TableCell>
                <TableCell className="text-[12px]">
                  {e.next_send_at ? new Date(e.next_send_at).toLocaleString() : "—"}
                </TableCell>
                <TableCell className="text-[12px] text-muted-foreground">
                  {new Date(e.started_at).toLocaleDateString()}
                </TableCell>
                <TableCell className="text-right space-x-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => setOpenHistory(openHistory === e.id ? null : e.id)}
                  >
                    History
                  </Button>
                  {e.status === "active" && (
                    <Button size="sm" variant="ghost" onClick={() => actMut.mutate({ id: e.id, action: "pause" })}>
                      <Pause className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {e.status === "paused" && (
                    <Button size="sm" variant="ghost" onClick={() => actMut.mutate({ id: e.id, action: "resume" })}>
                      <Play className="h-3.5 w-3.5" />
                    </Button>
                  )}
                  {["active", "paused"].includes(e.status) && (
                    <Button size="sm" variant="ghost" onClick={() => actMut.mutate({ id: e.id, action: "remove" })}>
                      <XIcon className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      {openHistory && <HistoryDrawer id={openHistory} onClose={() => setOpenHistory(null)} />}
    </div>
  );
}

function HistoryDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const histFn = useServerFn(getEnrollmentHistory);
  const { data, isLoading } = useQuery({
    queryKey: ["enrollment-history", id],
    queryFn: () => histFn({ data: { id } }),
  });

  return (
    <div className="fixed inset-0 z-50 flex justify-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/30" />
      <div
        className="relative w-full max-w-md bg-background border-l border-border h-full overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-[14px] font-semibold font-display">Enrollment history</h4>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <XIcon className="h-4 w-4" />
          </button>
        </div>
        {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
        {data && (
          <div className="space-y-4">
            <section>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Email events</div>
              <ul className="space-y-1.5">
                {data.events.length === 0 && <li className="text-[12px] text-muted-foreground">No events yet.</li>}
                {data.events.map((ev: any) => (
                  <li key={ev.id} className="text-[12px] flex justify-between gap-2">
                    <span>
                      <span className="font-medium">{ev.kind}</span>
                      {ev.meta?.reason && (
                        <span className="text-muted-foreground"> · {ev.meta.reason}</span>
                      )}
                    </span>
                    <span className="text-muted-foreground tabular-nums">
                      {new Date(ev.created_at).toLocaleString()}
                    </span>
                  </li>
                ))}
              </ul>
            </section>
            <section>
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground mb-1">Upgrade tokens</div>
              <ul className="space-y-1.5">
                {data.tokens.length === 0 && <li className="text-[12px] text-muted-foreground">None.</li>}
                {data.tokens.map((t: any) => (
                  <li key={t.id} className="text-[12px]">
                    <code className="text-[11px]">{t.token.slice(0, 12)}…</code>
                    {t.clicked_at && <span className="ml-2 text-emerald-700">clicked</span>}
                    {t.upgrade_completed_at && <span className="ml-2 text-emerald-700">upgraded</span>}
                    {t.revoked && <span className="ml-2 text-rose-700">revoked</span>}
                  </li>
                ))}
              </ul>
            </section>
          </div>
        )}
      </div>
    </div>
  );
}
